/**
 * Fetch the job rows for a ticket that is being rolled over / reassigned.
 *
 * Extracted into its own module deliberately: `EODRolloverContainer` imports
 * the very large `OptiPlaceModal`, whose type surface consumes enough of the
 * TypeScript instantiation budget that even a narrow `.from('jobs')` select
 * there trips TS2589 ("excessively deep"). The identical query type-checks
 * cleanly in a module that doesn't pull in that heavy graph, so the fetch lives
 * here and returns a small, explicit row shape the caller maps over.
 */
import { supabase } from '@/integrations/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export type RolloverSourceJob = Pick<
  Database['public']['Tables']['jobs']['Row'],
  | 'category'
  | 'submenu'
  | 'name'
  | 'standard_minutes'
  | 'bay_requirement'
  | 'skill_level'
  | 'proficiency_category'
>;

/**
 * OCT20 WS-ROLL (coordinator finding, with WS-CAT): this used to read `jobs` filtered by a `ticket_id` column that
 * the `jobs` catalog does not have — PostgREST refused it, so a reassignment from the end-of-day dialog always
 * carried NO jobs. The ticket's lines are `ticket_jobs` rows (joined to the catalog), and for a rollover day they
 * live on the chain's head (`rollover_job_owner`, 20270114100000). The minutes are the FOH estimate first
 * (FD-13: `minutes_override ?? standard_minutes`); `standard_minutes` below carries that effective value.
 */
export async function fetchRolloverSourceJobs(ticketId: string): Promise<RolloverSourceJob[]> {
  // The `jobs` table's postgrest generics are heavy enough that, near the program-wide type-instantiation ceiling,
  // even an isolated select trips TS2589. The widened client keeps `.from` from resolving the relationship graph;
  // the row shape is reasserted below against the generated Row type via RolloverSourceJob.
  const looseClient: SupabaseClient = supabase;
  const { data: owner } = await looseClient.rpc('rollover_job_owner', { p_ticket_id: ticketId });
  const lineTicket = typeof owner === 'string' && owner ? owner : ticketId;
  const { data, error } = await looseClient
    .from('ticket_jobs')
    .select('minutes_override, jobs!ticket_jobs_job_id_fkey ( category, submenu, name, standard_minutes, bay_requirement, skill_level, proficiency_category )')
    .eq('ticket_id', lineTicket);
  if (error) throw error;
  return ((data ?? []) as Array<{ minutes_override: number | null; jobs: RolloverSourceJob | RolloverSourceJob[] | null }>)
    .map((row) => {
      const job = Array.isArray(row.jobs) ? row.jobs[0] : row.jobs;
      if (!job) return null;
      return { ...job, standard_minutes: effectiveSourceJobMinutes(row.minutes_override, job.standard_minutes) };
    })
    .filter((j): j is RolloverSourceJob => j !== null);
}

/** FD-13: the FOH estimate entered on the ticket wins; the catalog time is only the illustrative default. */
export function effectiveSourceJobMinutes(minutesOverride: number | null | undefined, standardMinutes: number | null | undefined): number | null {
  return minutesOverride ?? standardMinutes ?? null;
}
